import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import {
  CreateTaskDto,
  RelatedEntityInput,
  TaskCategory,
  TaskStatus,
  TaskSubCategory,
} from 'src/types/task.types';

export const EMPTY_DESCRIPTION: SlateNode[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export const isDescriptionEmpty = (description: SlateNode[]) => {
  if (!description || description.length === 0) return true;
  return JSON.stringify(description) === JSON.stringify(EMPTY_DESCRIPTION);
};

export interface TaskFormValues {
  title: string;
  description: SlateNode[];
  status: TaskStatus;
  category?: TaskCategory | null;
  subCategory?: TaskSubCategory | null;
  dueDate?: Date | null;
  assignedToId?: string | null;
  relatedEntities: RelatedEntityInput[];
  requiresOnSite: boolean;
}

export interface UseCreateTaskFormOptions {
  initialCommunityFeedItemId?: string | null;
  initialProjectId?: string | null;
}

export const useCreateTaskForm = (options?: UseCreateTaskFormOptions) => {
  const { t } = useTranslation();

  const initialRelatedEntities: RelatedEntityInput[] = [];
  if (options?.initialCommunityFeedItemId) {
    initialRelatedEntities.push({
      type: 'communityFeedItem',
      id: options.initialCommunityFeedItemId,
    });
  }
  if (options?.initialProjectId) {
    initialRelatedEntities.push({ type: 'project', id: options.initialProjectId });
  }

  const form = useForm<TaskFormValues, (values: TaskFormValues) => CreateTaskDto>({
    initialValues: {
      title: '',
      description: EMPTY_DESCRIPTION,
      status: TaskStatus.TODO,
      category: null,
      dueDate: null,
      subCategory: null,
      assignedToId: null,
      relatedEntities: initialRelatedEntities,
      requiresOnSite: false,
    },
    validate: {
      title: (value: string) =>
        value.length < 2 ? t('common.errors.tooShort', 'Trop court') : null,
    },
    transformValues: (values: TaskFormValues) => {
      return {
        title: values.title,
        description: isDescriptionEmpty(values.description)
          ? undefined
          : JSON.stringify(values.description),
        status: values.status,
        category: values.category ?? undefined,
        subCategory: values.subCategory ?? undefined,
        dueDate: values.dueDate?.toISOString() ?? undefined,
        assignedToId: values.assignedToId ?? undefined,
        relatedEntities: values.relatedEntities.length > 0 ? values.relatedEntities : undefined,
        requiresOnSite: values.requiresOnSite,
      } as CreateTaskDto;
    },
  });

  return { form };
};
